import { getUnwrappedToken } from '@morpho-org/blue-sdk'
import { isDefined } from '@morpho-org/morpho-ts'
import { type Address, isAddressEqual, type MulticallResponse } from 'viem'

import { getStakedTokens, type StakedTokenConfig } from './stakedTokens'

export interface UserHolding {
  token: {
    address: Address
    id: string
    symbol: string
  }
  balance: bigint
}

export interface AssetItem {
  id: string
  address: Address
  symbol: string
}

export interface ProcessedAssets {
  allAssets: AssetItem[]
  stakedTokenConfig: StakedTokenConfig[]
}

/**
 * Creates a UserHolding array for native token balance (e.g., ETH).
 * @param nativeBal - Native token balance
 * @param wNativeToken - Wrapped native token asset info
 * @returns Array with single holding if balance > 0, empty array otherwise
 */
export function getNativeHolding(nativeBal: bigint, wNativeToken?: AssetItem): UserHolding[] {
  if (nativeBal > 0n && wNativeToken) {
    return [
      {
        token: {
          address: wNativeToken.address,
          id: wNativeToken.id,
          symbol: wNativeToken.symbol
        },
        balance: nativeBal
      }
    ]
  }
  return []
}

/**
 * Converts ERC20 contract call results into UserHolding objects.
 * Filters out zero balances and maps results to asset metadata.
 * @param erc20Results - Array of contract call results containing token balances
 * @param allAssets - Array of asset metadata to map against
 * @returns Array of UserHolding objects with non-zero balances
 */
export function getErc20Holdings(
  erc20Results: readonly Pick<MulticallResponse<bigint>, 'result'>[],
  allAssets: AssetItem[]
): UserHolding[] {
  return erc20Results
    .map((res, i) => {
      const asset = allAssets[i]
      if (!asset) return null
      const balance = res.result ?? 0n
      if (balance === 0n) return null
      return {
        token: {
          address: asset.address,
          id: asset.id,
          symbol: asset.symbol
        },
        balance
      }
    })
    .filter(isDefined)
}

/**
 * Creates UserHolding entries for staked token equivalents based on source token holdings.
 * Maps holdings of source tokens to their staked variants (e.g., ETH -> stETH).
 * @param baseHoldings - User's current token holdings
 * @param stakedTokenConfig - Configuration mapping source tokens to staked tokens
 * @param assets - Available asset metadata
 * @returns Array of UserHolding objects for staked token equivalents
 */
export function getStakedTokenHoldings(
  baseHoldings: UserHolding[],
  stakedTokenConfig: StakedTokenConfig[],
  assets: AssetItem[]
): UserHolding[] {
  return stakedTokenConfig
    .map(config => {
      const sourceHolding = baseHoldings.find(
        h => isAddressEqual(h.token.address, config.sourceToken) && h.balance > 0n
      )
      const stakedAsset = assets.find(a => isAddressEqual(a.address, config.stakedToken))
      if (sourceHolding && stakedAsset) {
        return {
          token: {
            id: stakedAsset.id,
            address: stakedAsset.address,
            symbol: stakedAsset.symbol
          },
          balance: sourceHolding.balance
        }
      }
      return null
    })
    .filter(isDefined)
}

/**
 * Processes asset list to include wrapped and staked token variants.
 * Expands the asset array to include unwrapped and staked versions of tokens.
 * @param assets - Base array of assets
 * @param targetChainId - Chain ID to determine unwrapped/staked variants
 * @returns Processed assets with additional variants and staked token config
 */
export function processAssetsForHoldings(
  assets: AssetItem[],
  targetChainId: number
): ProcessedAssets {
  const stakedTokenConfig = getStakedTokens(targetChainId)

  const processedAssets = assets.reduce<AssetItem[]>((acc, currentAsset) => {
    acc.push(currentAsset)

    const unwrapped = getUnwrappedToken(currentAsset.address, targetChainId)
    if (unwrapped) {
      acc.push({
        ...currentAsset,
        address: unwrapped
      })
    }

    const stakedToken = stakedTokenConfig.find(config =>
      isAddressEqual(config.sourceToken, currentAsset.address)
    )
    if (stakedToken) {
      acc.push({
        ...currentAsset,
        address: stakedToken.stakedToken
      })
    }

    return acc
  }, [])

  return { allAssets: processedAssets, stakedTokenConfig }
}
